import { gql } from '@apollo/client';
import {
  CounterSource,
  CounterType,
  MachineExtraDataFieldType,
  MachineGroup,
  OwnershipType,
} from '@/generated/codegen';

export const BC_DEVICE_FIELDS = gql`
  fragment BilderitConnectDeviceFields on BilderitConnectDevice {
    id
    telemetry_provider
    aemp_provider
    company {
      id
      name
    }
    counter_offset
    created_at
    flespi_id
    serial_number
    machine {
      ...MachineLinkFields
      serial_number
      preferred_counter_type
    }
  }
`;

export const ownershipTypeOptions = [
  { label: 'Propio', value: OwnershipType.Owned },
  { label: 'Leasing', value: OwnershipType.Leased },
  { label: 'Alquilado', value: OwnershipType.Rented },
  { label: 'De Socios', value: OwnershipType.PartnerOwned },
  { label: 'Otros', value: OwnershipType.Other },
];

export const COUNTER_TYPE_OPTIONS = [
  { label: 'Horas', value: CounterType.Hs },
  { label: 'Kilómetros', value: CounterType.Km },
];

export const machineGroupToCounterType: Record<
  MachineGroup,
  CounterType | undefined
> = {
  [MachineGroup.EquipoPesado]: CounterType.Hs,
  [MachineGroup.Movilidad]: CounterType.Km,
  [MachineGroup.Camion]: undefined,
  [MachineGroup.ActivoFijo]: undefined,
};

export const machineExtraDataTypeOptions = [
  { label: 'Número', value: MachineExtraDataFieldType.Decimal },
  { label: 'Texto', value: MachineExtraDataFieldType.Text },
  { label: 'Fecha y Hora', value: MachineExtraDataFieldType.DateTime },
  { label: 'Si / No', value: MachineExtraDataFieldType.Boolean },
];

export const machineGroupOptions = [
  {
    label: 'Equipos Pesados',
    value: MachineGroup.EquipoPesado,
  },
  {
    label: 'Camiones',
    value: MachineGroup.Camion,
  },
  {
    label: 'Movilidades',
    value: MachineGroup.Movilidad,
  },
  {
    label: 'Activos Fijos',
    value: MachineGroup.ActivoFijo,
  },
];

export const machineGroupToLabel = {
  [MachineGroup.EquipoPesado]: 'Equipo Pesado',
  [MachineGroup.Camion]: 'Camión',
  [MachineGroup.Movilidad]: 'Movilidad',
  [MachineGroup.ActivoFijo]: 'Activo Fijo',
};

export const machineGroupToLabelPlural = {
  [MachineGroup.EquipoPesado]: 'Equipos Pesados',
  [MachineGroup.Camion]: 'Camiones',
  [MachineGroup.Movilidad]: 'Movilidades',
  [MachineGroup.ActivoFijo]: 'Activos Fijos',
};

export const machineGroupToUrl = {
  [MachineGroup.ActivoFijo]: 'fixed-assets',
  [MachineGroup.Camion]: 'trucks',
  [MachineGroup.EquipoPesado]: 'machines',
  [MachineGroup.Movilidad]: 'vehicles',
};

export const counterSourceToLabel = {
  [CounterSource.OosReport]: 'Reporte Fuera de Servicio',
  [CounterSource.UsageReport]: 'Parte Diario',
  [CounterSource.FuelReport]: 'Despacho de Combustible',
  [CounterSource.MachineNotice]: 'Novedad',
  [CounterSource.ServiceReport]: 'Reporte de Service',
  [CounterSource.Telemetry]: 'Telemetría',
  [CounterSource.MoveReport]: 'Checkin/Checkout',
  [CounterSource.Checklist]: 'Checklist',
};

export const MACHINE_COUNTER_FIELDS = gql`
  fragment MachineCounterFields on MachineCounter {
    value
    datetime
    source
    type
  }
`;

export const MACHINE_FIELDS = gql`
  fragment MachineFields on Machine {
    id
    is_archived
    name
    external_source {
      provider
      sync_status
    }
    display_name @client
    display_type @client
    type {
      __typename
      id
      type
      group
      subtype
      reported_daily
      accepts_usage
      accepts_service
      accepts_oos
      accepts_available
    }
    power
    weight
    brand
    model
    year
    owner
    ownership_type
    ownership_type_other
    extra_data
    domain
    preferred_counter_type
    last_counters {
      ...MachineCounterFields
    }
    last_counter_from_usage_report @client {
      ...MachineCounterFields
    }
    last_counter_from_fuel_report @client {
      ...MachineCounterFields
    }
    counter {
      end
      start
      type
      value
    }
    fuel_type
    fuel_participation
    fuel_capacity
    last_known_flowmeter
    last_known_flowmeter_on
    last_computed_fuel_stock
    last_computed_fuel_stock_on
    last_measured_fuel_stock
    last_measured_fuel_stock_on
    serial_number
    engine_number
    replacement_value
    last_status
    last_status_on
    observations
    maintenance_plan {
      id
      title
      type
      base_period
      period_type
      programs {
        id
        title
        multiplier
      }
    }
    assigned {
      __typename
      id
      first_name
      last_name
      full_name @client
      dni
    }
    company {
      id
    }
    project {
      id
      name
      business_unit {
        id
        name
      }
    }
    next_mp_sequence_index

    bilderit_connect_devices {
      ...BilderitConnectDeviceFields
    }
  }
`;

export const MACHINE_PICKER_FIELDS = gql`
  fragment MachinePickerFields on Machine {
    id
    name
    domain
    serial_number
    preferred_counter_type
    fuel_type
    fuel_participation
    fuel_capacity
    last_known_flowmeter
    type {
      __typename
      id
      type
      group
      accepts_usage
    }
    company {
      __typename
      id
    }
    project {
      __typename
      id
    }
    last_counters {
      ...MachineCounterFields
    }
    last_counter_from_usage_report @client {
      ...MachineCounterFields
    }
    last_counter_from_fuel_report @client {
      ...MachineCounterFields
    }
    maintenance_plan {
      id
      title
      type
      base_period
      period_type
      programs {
        id
        title
        multiplier
      }
    }
  }
`;

export const MACHINE_LINK_FIELDS = gql`
  fragment MachineLinkFields on Machine {
    id
    company {
      id
    }
    project {
      id
      name
    }
    is_archived
    name
    display_name @client
    bilderit_connect_devices {
      id
    }
  }
`;

export const MACHINE_EXTRA_DATA_FIELDS = gql`
  fragment MachineExtraDataFields on CompanyMachineExtraData {
    id
    created_at
    updated_at
    machine_group
    field_name
    field_type
    unit
    is_required
  }
`;

export const ADD_HEAVY_EQUIPMENT_INPUT = [
  'name',
  'company_id',
  'project_id',
  'type_id',
  'subtype',
  'power',
  'brand',
  { name: 'counter', schema: ['start', 'end', 'type'] },
  'model',
  'owner',
  'ownership_type',
  'ownership_type_other',
  'extra_data',
  'year',
  'fuel_capacity',
  'replacement_value',
  'weight',
  'domain',
  'serial_number',
  'engine_number',
  'service_period',
  'service_period_type',
  'mp_id',
  'observations',
  'photo_ids',
];

export const UPDATE_HEAVY_EQUIPMENT_INPUT = [
  'name',
  'type_id',
  'subtype',
  'power',
  'brand',
  { name: 'counter', schema: ['start', 'end', 'type'] },
  'model',
  'owner',
  'ownership_type',
  'ownership_type_other',
  'extra_data',
  'year',
  'fuel_capacity',
  'replacement_value',
  'weight',
  'domain',
  'serial_number',
  'engine_number',
  'service_period',
  'service_period_type',
  'mp_id',
  'observations',
  'photo_ids',
];

export const DELETE_MACHINE = gql`
  mutation DeleteMachine($machineId: Int!) {
    deleteMachine(id: $machineId) {
      ... on SuccessResult {
        result
      }
      ... on ServerError {
        message
      }
      ... on Error {
        message
      }
    }
  }
`;

export const ARCHIVE_MACHINE = gql`
  mutation ArchiveMachine($machineId: Int!) {
    archiveMachine(machineId: $machineId) {
      ...MachineFields
    }
  }
`;

export const UNARCHIVE_MACHINE = gql`
  mutation UnarchiveMachine($input: UnarchiveMachineInput!) {
    unarchiveMachine(input: $input) {
      ...MachineFields
    }
  }
`;

export const ADD_HEAVY_EQUIPMENT = gql`
  mutation AddHeavyEquipment($input: AddHeavyEquipmentInput!) {
    addHeavyEquipment(input: $input) {
      ... on Machine {
        ...MachineFields
      }
      ... on Error {
        message
      }
    }
  }
`;

export const UPDATE_HEAVY_EQUIPMENT = gql`
  mutation UpdateHeavyEquipment($id: Int!, $input: UpdateHeavyEquipmentInput!) {
    updateHeavyEquipment(id: $id, input: $input) {
      ... on Machine {
        ...MachineFields
        ...ServicesFields
      }
      ... on Error {
        message
      }
    }
  }
`;

export const MOVE_MACHINES = gql`
  mutation MoveMachine($input: MoveMachineInput!) {
    moveMachine(input: $input) {
      ...MachineFields
    }
  }
`;

export const GET_MACHINE_BY_NAME = gql`
  query GetMachinesByName(
    $context: Context
    $filter: FilterMachine
    $pagination: OffsetPaginationInput
  ) {
    getMachines(context: $context, filter: $filter, pagination: $pagination) {
      ... on AuthCheckGetMachines {
        page_info {
          total_items
        }
        items {
          ...MachineFields
        }
      }
      ... on Error {
        message
      }
    }
  }
`;

export const GET_MACHINES_FOR_PICKER = gql`
  query GetMachinesForPicker(
    $context: Context
    $filter: FilterMachine
    $pagination: OffsetPaginationInput
  ) {
    getMachines(context: $context, filter: $filter, pagination: $pagination) {
      ... on AuthCheckGetMachines {
        page_info {
          total_items
        }
        items {
          ...MachinePickerFields
        }
      }
      ... on Error {
        message
      }
    }
  }
`;

export const GET_MACHINE_LINK_BY_ID = gql`
  query GetMachineLinkById($id: Int!) {
    getMachineById(id: $id) {
      ... on AuthCheckGetMachineById {
        item {
          ...MachineLinkFields
        }
      }
      ... on Error {
        message
      }
    }
  }
`;

export const GET_MACHINE_BY_ID = gql`
  query GetMachineById($id: Int!) {
    getMachineById(id: $id) {
      ... on AuthCheckGetMachineById {
        item {
          ...MachineFields
        }
      }
      ... on Error {
        message
      }
    }
  }
`;

export const GET_NEXT_SERVICE_BY_MACHINE_ID = gql`
  query GetNextServiceByMachineId($id: Int!) {
    getMachineById(id: $id) {
      ... on AuthCheckGetMachineById {
        item {
          id
          next_services(limit: 1) {
            ...NextServiceFields
          }
        }
      }
      ... on Error {
        message
      }
    }
  }
`;

export const GET_MACHINE_PHOTOS_BY_ID = gql`
  query GetMachinePhotosById($id: Int!) {
    getMachineById(id: $id) {
      ... on AuthCheckGetMachineById {
        item {
          id
          photos {
            id
            url
          }
        }
      }
      ... on Error {
        message
      }
    }
  }
`;

export const GET_ALL_MACHINES = gql`
  query GetMachines($context: Context, $pagination: OffsetPaginationInput) {
    getMachines(context: $context, pagination: $pagination) {
      ... on AuthCheckGetMachines {
        items {
          ...MachineFields
        }
      }
      ... on Error {
        message
      }
    }
  }
`;

export const GET_ALL_ARCHIVED_MACHINES = gql`
  query GetAllArchivedMachines($context: Context) {
    getMachines(context: $context, filter: { is_archived: true }) {
      ... on AuthCheckGetMachines {
        items {
          ...MachineFields
        }
      }
      ... on Error {
        message
      }
    }
  }
`;

export const GET_MACHINE_GROUP_COUNTS = gql`
  query GetMachineGroupCounts($context: Context) {
    getMachineGroupCounts(context: $context) {
      ... on AuthCheckGetMachineGroupCounts {
        items {
          group
          count
        }
      }
      ... on Error {
        message
      }
    }
  }
`;

export const GET_ALL_HEAVY_EQUIPMENT = gql`
  query GetHeavyEquipment($context: Context) {
    getMachines(context: $context, filter: { group: EQUIPO_PESADO }) {
      ... on AuthCheckGetMachines {
        items {
          ...MachineFields
        }
      }
      ... on Error {
        message
      }
    }
  }
`;

export const GET_UPLOAD_MACHINES_URL = gql`
  query GetUploadMachinesPresignatedUrl($companyId: Int!) {
    getUploadMachinesPresignatedUrl(companyId: $companyId) {
      ... on AuthCheckGetUploadMachinesPresignatedUrl {
        item {
          uploadUrl
          templateUrl
          statusId
        }
      }
      ... on Error {
        message
      }
    }
  }
`;

export const GET_UPLAOD_MACHINES_STATUS = gql`
  query GetUploadStatus($statusId: ID!) {
    getUploadStatus(statusId: $statusId) {
      type
      info
    }
  }
`;

export const UPLOAD_MACHINES_STATUS_CHANGED = gql`
  subscription UploadStatusChanged($statusId: ID!) {
    uploadStatusChanged(statusId: $statusId) {
      type
      info
    }
  }
`;

export const GET_MACHINE_TYPES = gql`
  query GetMachineTypes {
    getMachineTypes {
      ... on AuthCheckGetMachineTypes {
        items {
          ...MachineTypeFields
        }
      }
      ... on Error {
        message
      }
    }
  }
  fragment MachineTypeFields on MachineType {
    id
    type
    group
  }
`;

export const GET_MACHINES_LIST_CSV = gql`
  query GetMachinesListCSV(
    $company_id: Int!
    $project_id: Int
    $group: MachineGroup
    $is_archived: Boolean
    $machine_ids: [Int!]
    $columns: [String!]
  ) {
    downloadMachinesWorkbook(
      company_id: $company_id
      project_id: $project_id
      group: $group
      is_archived: $is_archived
      machine_ids: $machine_ids
      columns: $columns
    ) {
      ... on SuccessResult {
        result
      }
      ... on ServerError {
        message
      }
      ... on Error {
        message
      }
    }
  }
`;

export const SET_MACHINE_FUEL_CAPACITY = gql`
  mutation SetMachineFuelCapacity($input: SetMachineFuelCapacityInput!) {
    setMachineFuelCapacity(input: $input) {
      ...MachineFields
    }
  }
`;

export const GET_MACHINE_EXTRA_DATA_BY_COMPANY_ID = gql`
  query GetMachineExtraDataByCompanyId($companyId: Int!) {
    getCompanyMachineExtraData(companyId: $companyId) {
      ...MachineExtraDataFields
    }
  }
`;

export const BATCH_UPSERT_MACHINE_EXTRADATA = gql`
  mutation BatchUpsertMachineExtraData(
    $input: [AddCompanyMachineExtraDataInput!]!
  ) {
    batchUpsertCompanyMachineExtraData(input: $input) {
      ...MachineExtraDataFields
    }
  }
`;

export const BATCH_DELETE_MACHINE_EXTRADATA = gql`
  mutation BatchDeleteMachineExtraData($ids: [ID!]!) {
    batchDeleteCompanyMachineExtraData(ids: $ids)
  }
`;

export const GET_MACHINE_PROJECT_AT_DATE = gql`
  query GetMachineProjectAtDate($machineId: Int!, $date: LocalDate!) {
    getMachineProjectsAtDate(machineId: $machineId, date: $date) {
      id
      name
    }
  }
`;
